import { AgentCompletedEducationDetailHistoryItemsMap } from "@/components/history/items/agentCompletedEdicationDetailHistoryItems";
import { HistoryItem, HistoryRecordType, type LabelledHistoryItem } from "@/components/history/types";
import { formatAddress } from "@/utils/formatUtils";
import { isEmptyOrUndefined } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import type { Address, Contact } from "../../types";
import { ContactsView } from "../contact/ContactsView";
import { AgentHistoryItemsMap } from "./items/agentHistoryItems";
import { ClientHistoryItemsMap } from "./items/clientHistoryItems";
import { CommissionsSettingsHistoryItemsMap } from "./items/commissionsSettingsHistoryItems";
import { EmptyHistoryItem } from "./items/commonHistoryItems";
import { ContractHistoryItemsMap } from "./items/contractHistoryItems";
import { VehicleHistoryItemsMap } from "./items/vehicleHistoryItems";

type HistoryItemRenderer = Map<string, (value: any) => React.JSX.Element>;

// isLabelledHistoryItem compares the key count, so the list has to cover every key of the
// interface - Record<keyof T, unknown> makes the compiler reject the literal when a field is added
const labelledHistoryItemKeys = Object.keys({ id: 0, label: 0 } satisfies Record<keyof LabelledHistoryItem, unknown>);

// These two are deliberately only a discriminating subset, not the full key list
const contactKeys: (keyof Contact)[] = ["type", "value", "marketingConsent"];
const addressKeys: (keyof Address)[] = ["city", "zipCode", "country"];

const isObjectWithKeys = (input: any, keys: string[]) =>
  !!input && typeof input === "object" && keys.every((key) => key in input);

export const isLabelledHistoryItem = (input: any): input is LabelledHistoryItem =>
  isObjectWithKeys(input, labelledHistoryItemKeys) && Object.keys(input).length === labelledHistoryItemKeys.length;

export const isArrayOfContacts = (input: any): input is Contact[] =>
  Array.isArray(input) && input.length > 0 && input.every((item) => isObjectWithKeys(item, contactKeys));

export const isAddress = (input: any): input is Address =>
  isObjectWithKeys(input, addressKeys) && ("descriptiveNumber" in input || "orientationNumber" in input);

interface Props {
  item?: HistoryItem;
  recordType?: HistoryRecordType;
}

export const HistoryItemValueView = ({ item, recordType }: Props) => {
  const { t } = useTranslation();

  if (!item || isEmptyOrUndefined(item.value)) {
    return <EmptyHistoryItem />;
  }

  const { value, fieldName } = item;

  const unsupportedValue = <span>{t("history.helpers.unsupportedValueType")}</span>;

  const renderHistoryRecordStringValue = (renderer: HistoryItemRenderer) => {
    const item = renderer.get(fieldName);
    return renderer.has(fieldName) && item ? item(value) : <span>{value}</span>;
  };

  const renderHistoryRecordObjectValue = (renderer: HistoryItemRenderer) => {
    const item = renderer.get(fieldName);
    return renderer.has(fieldName) && item ? item(value) : unsupportedValue;
  };

  if (typeof value !== "object") {
    switch (recordType) {
      case "contract":
        return renderHistoryRecordStringValue(ContractHistoryItemsMap);
      case "agent":
        return renderHistoryRecordStringValue(AgentHistoryItemsMap);
      case "client":
        return renderHistoryRecordStringValue(ClientHistoryItemsMap);
      case "commissions-settings":
        return renderHistoryRecordStringValue(CommissionsSettingsHistoryItemsMap);
      case "agent-completed-education-detail":
        return renderHistoryRecordStringValue(AgentCompletedEducationDetailHistoryItemsMap);
      case "vehicle":
        return renderHistoryRecordStringValue(VehicleHistoryItemsMap);
      default:
        return unsupportedValue;
    }
  }

  if (isLabelledHistoryItem(value)) {
    return <span>{value.label}</span>;
  } else if (isAddress(value)) {
    return <span>{formatAddress(value)}</span>;
  } else if (isArrayOfContacts(value)) {
    return (
      <ContactsView
        contacts={value}
        marketingConsentsAreApplicable={recordType === "client"}
        size={recordType === "client" ? "small" : "normal"}
      />
    );
  } else {
    switch (recordType) {
      case "contract":
        return renderHistoryRecordObjectValue(ContractHistoryItemsMap);
      case "agent":
        return renderHistoryRecordObjectValue(AgentHistoryItemsMap);
      case "client":
        return renderHistoryRecordObjectValue(ClientHistoryItemsMap);
      case "commissions-settings":
        return renderHistoryRecordObjectValue(CommissionsSettingsHistoryItemsMap);
      default:
        return unsupportedValue;
    }
  }
};
